'use client';

/**
 * Site-wide announcement popup — fetched from `/api/announcements/active`
 * and rendered as a centered modal that "pops in" with a soft scale +
 * fade animation.
 *
 * Per-user dismissals live in `localStorage['ps_dismissed_announcements']`
 * as an array of announcement IDs. Once a viewer dismisses an announcement
 * it never shows again to that browser. (Re-show on edit by changing the
 * key to `${id}:${updated_at}` if you ever need that.)
 *
 * Why popup not banner:
 *   - Inline banner ate the top of every page. Users learnt to skip it.
 *   - Centered modal forces a moment of attention — useful for the
 *     intentionally-rare cases admins post an announcement (downtime,
 *     policy change, new feature launch).
 *
 * Queueing: if multiple announcements are active, we show only ONE at a
 * time (the newest non-dismissed). After the user dismisses it, the next
 * one in line surfaces on the NEXT page navigation — never two popups in
 * a row in the same session, by design.
 */
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth-context';
import { getApiBaseUrl } from '@/lib/api-url';

interface ActiveAnnouncement {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'success' | 'warning' | 'alert';
  is_dismissible: boolean;
  cta_label?: string | null;
  cta_href?: string | null;
}

const DISMISS_KEY = 'ps_dismissed_announcements';
const BASE_URL = getApiBaseUrl();


function loadDismissed(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...ids])); }
  catch { /* quota / disabled */ }
}


export default function AnnouncementBanner() {
  const { user, isLoading } = useAuth();
  const [items, setItems] = useState<ActiveAnnouncement[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());

  useEffect(() => {
    if (isLoading) return;  // wait until we know whether the viewer is authed
    // The endpoint is public but accepts our token (so it can filter to
    // the right audience). Use the token if we have one.
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    const token = typeof window !== 'undefined' ? localStorage.getItem('prompt_gallery_token') : null;
    if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    fetch(`${BASE_URL}/announcements/active`, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: ActiveAnnouncement[]) => setItems(rows))
      .catch(() => { /* silent */ });
  }, [isLoading, user?.id]);

  const visible = useMemo(
    () => items.filter((a) => !dismissed.has(a.id)),
    [items, dismissed],
  );

  if (visible.length === 0) return null;

  // Show only the newest active+undismissed announcement as a centered popup.
  // Older ones queue and surface on the next page navigation. Spamming
  // multiple popups at once is annoying — one at a time matches what users
  // expect from "important notice" UX patterns (cookie banner, login modal).
  const current = visible[0];

  const handleDismiss = (id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(next);
      return next;
    });
  };

  return <AnnouncementPopup a={current} onDismiss={handleDismiss} />;
}


function AnnouncementPopup({
  a, onDismiss,
}: {
  a: ActiveAnnouncement;
  onDismiss: (id: string) => void;
}) {
  // Lock body scroll while the popup is open + dismiss on Escape, just like
  // the confirm dialog. Clicking the backdrop also dismisses (counts as
  // "seen" — same as clicking ✕). Non-dismissible announcements keep both
  // paths but call onDismiss conditionally below.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && a.is_dismissible) {
        e.preventDefault();
        onDismiss(a.id);
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [a.id, a.is_dismissible, onDismiss]);

  // Severity → accent token. Used on the icon chip + CTA button so the
  // brand stays consistent with toasts (same severity colors there).
  const palette = {
    info:    { accent: '#38bdf8', icon: 'campaign'      },  // cyan
    success: { accent: '#22c55e', icon: 'check_circle' },   // emerald
    warning: { accent: '#f59e0b', icon: 'warning'      },   // amber
    alert:   { accent: '#ef4444', icon: 'error'        },   // red
  }[a.severity];

  const safeCtaHref = sanitizeAnnouncementCta(a.cta_href);
  const ctaIsExternal = safeCtaHref ? /^https?:\/\//i.test(safeCtaHref) : false;

  const close = () => { if (a.is_dismissible) onDismiss(a.id); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`announcement-title-${a.id}`}
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: 95,
        display: 'grid', placeItems: 'center',
        padding: 16,
        background: 'color-mix(in oklch, black 50%, transparent)',
        backdropFilter: 'blur(6px)',
        animation: 'ps-modal-fade 0.2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          padding: 0,
          width: 480,
          maxWidth: '100%',
          overflow: 'hidden',
          // Subtle pop-in: scale from 0.92 + slight upward translate.
          // Defined in globals.css alongside ps-modal-slide.
          animation: 'ps-modal-slide 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Top accent bar — adopts severity color so the dialog is
            recognisable at a glance, even before the user reads the title. */}
        <div style={{ height: 4, background: palette.accent }} />

        <div style={{ padding: '24px 24px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div
              aria-hidden
              style={{
                flexShrink: 0,
                width: 44, height: 44,
                borderRadius: '50%',
                display: 'grid', placeItems: 'center',
                background: `${palette.accent}1f`,   // 12% opacity tint
                color: palette.accent,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 24 }}>
                {palette.icon}
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                id={`announcement-title-${a.id}`}
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 20, fontWeight: 700,
                  margin: 0, lineHeight: 1.3,
                  color: 'var(--ink)',
                  textWrap: 'balance',
                }}
              >
                {a.title}
              </h2>
              <p style={{
                marginTop: 8, marginBottom: 0,
                fontSize: 14, lineHeight: 1.55,
                color: 'var(--ink-2)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {a.body}
              </p>
            </div>
            {a.is_dismissible && (
              <button
                type="button"
                aria-label="Dismiss"
                onClick={close}
                className="ps-btn ps-btn-icon ps-btn-ghost ps-btn-sm"
                style={{ flexShrink: 0, marginTop: -4, marginRight: -4 }}
              >
                <span className="material-symbols-outlined ps-icon">close</span>
              </button>
            )}
          </div>

          {(safeCtaHref && a.cta_label) || a.is_dismissible ? (
            <div
              style={{
                marginTop: 22, display: 'flex',
                gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap',
              }}
            >
              {a.is_dismissible && (
                <button
                  type="button"
                  onClick={close}
                  className="ps-btn ps-btn-ghost ps-btn-sm"
                >
                  Got it
                </button>
              )}
              {safeCtaHref && a.cta_label && (
                ctaIsExternal ? (
                  <a
                    href={safeCtaHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}     // CTA also counts as "seen"
                    className="ps-btn ps-btn-primary ps-btn-sm no-underline"
                    style={{ background: palette.accent, borderColor: palette.accent }}
                  >
                    {a.cta_label}
                    <span className="material-symbols-outlined" style={{ fontSize: 14, marginLeft: 4 }}>
                      open_in_new
                    </span>
                  </a>
                ) : (
                  <Link
                    href={safeCtaHref}
                    onClick={close}
                    className="ps-btn ps-btn-primary ps-btn-sm no-underline"
                    style={{ background: palette.accent, borderColor: palette.accent }}
                  >
                    {a.cta_label}
                    <span className="material-symbols-outlined" style={{ fontSize: 14, marginLeft: 4 }}>
                      arrow_forward
                    </span>
                  </Link>
                )
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Whitelist for the announcement CTA href: an internal path like `/foo`,
 * or an absolute http(s):// URL. Rejects `javascript:`, `data:`,
 * protocol-relative `//evil.com`, control chars, and anything else.
 *
 * Mirrors the backend `is_safe_link_href` validator. Returns null for
 * unsafe/empty input — caller treats null as "no CTA".
 */
function sanitizeAnnouncementCta(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const v = raw.trim();
  if (!v) return null;
  if (/[\r\n\t]/.test(v)) return null;
  if (v.length > 500) return null;
  // Internal path: starts with single slash + non-slash. `//evil.com` is
  // protocol-relative and would silently navigate to a foreign origin.
  if (/^\/[^/]/.test(v)) return v;
  if (v === '/') return v;
  // Absolute URL — must parse cleanly with http/https scheme + non-empty host.
  if (!/^https?:\/\//i.test(v)) return null;
  try {
    const u = new URL(v);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (!u.host) return null;
    return u.toString();
  } catch {
    return null;
  }
}
